import { useQueryClient } from '@tanstack/react-query'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { CardInfo, Tabs } from '@/components'
import {
  Button,
  CalendarIcon,
  Skeleton,
  StarsIcon,
  Text,
  Tooltip,
} from '@mercai/clever'
import { QuotationMetrics, QuotationStatusSelect } from '../../components'
import { DuplicateQuotationModal, PendingApprovalModal } from '../components'

import {
  AiData,
  History,
  QuotationSummaryDelivery,
  QuotationSummaryProducts,
  QuotationSummaryProfile,
  QuotationSummaryStateHistory,
  QuotationSummaryValues,
} from './components'

import { appQuotationLink } from '@/constants'
import {
  calculateQuotationValues,
  isPendingApprovalQuotationApprover,
  onErrorMessage,
  onSuccessMessage,
  trackEvent,
  useCookieState,
  useParamsState,
} from '@/helpers'
import { useAuth } from '@/providers'
import { ROUTES_ADMIN } from '@/router'
import {
  QUERIES_ADMIN,
  useCalculateMargin,
  useDuplicateQuotation,
  useQuotationDetail,
} from '@/services'
import { getQuotationMetricsPayload } from '../../helpers'
import { formatDate } from './show.constants'

import { CalculateMarginPayloadProps, QuotationProps } from '@/types'
import styles from './show.module.css'

export const QuotationV2Show = () => {
  const { t } = useTranslation('screenQuotationV2Show')
  const queryClient = useQueryClient()
  const { uuid } = useParams()
  const { whoAmI } = useAuth()
  const navigate = useNavigate()

  const [isOpenDuplicateModal, setIsOpenDuplicateModal] = useState(false)

  const location = useLocation()
  const page = location.pathname.split('/').pop()

  const [queryParams, setQueryParams] = useParamsState({
    tab: 'all',
  })

  const [dontShowDuplicateWarning] = useCookieState<boolean>(
    'dont-show-duplicate-quotation-warning',
    false,
  )

  const handleParams = (changedKey: string, newValue: string | number) => {
    setQueryParams({ ...queryParams, [changedKey]: `${newValue}` })
  }

  const { data: dataQuotation, isLoading } = useQuotationDetail(uuid || '')

  const { mutateAsync: mutateDuplicate } = useDuplicateQuotation()

  const isPending = dataQuotation?.data?.status === 'pending'
  const isCreated = dataQuotation?.data?.status === 'created'
  const isPendingApproval = dataQuotation?.data?.status === 'pending_approval'
  const hasPendingApprovalPermission = isPendingApprovalQuotationApprover(
    whoAmI?.email || '',
  )

  const [isOpenPendingApproval, setIsOpenPendingApproval] = useState(false)

  const dataForMetrics = useMemo(() => {
    if (dataQuotation?.data) {
      return getQuotationMetricsPayload({
        quotationValues: dataQuotation?.data as QuotationProps,
      })
    }
    return {
      customsFreightCost: undefined,
    } as Partial<CalculateMarginPayloadProps>
  }, [dataQuotation])

  const { discount, iva, subTotal, total } = calculateQuotationValues(
    dataQuotation?.data?.products || [],
    dataQuotation?.data?.deliveryOption === 'pick_up'
      ? 0
      : +(dataQuotation?.data?.totalFreightPrice || 0),
    +(dataQuotation?.data?.discount || 0),
    dataQuotation?.data?.includeIva,
  )

  const onHandleDuplicateQuotation = async () => {
    if (!dataQuotation?.data) return

    const result = await mutateDuplicate(dataQuotation?.data)

    if (result.success) {
      onSuccessMessage(
        t('quotationShowDuplicateSuccessTitle'),
        t('quotationShowDuplicateSuccessMessage'),
      )

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.QUOTATION.module],
      })

      if (result?.data?.uuid) {
        navigate(
          ROUTES_ADMIN.BUSINESS.QUOTATION.UPDATE.fullPath(result.data.uuid),
        )
      }
    } else {
      onErrorMessage(
        t('quotationShowDuplicateErrorTitle'),
        t('quotationShowDuplicateErrorMessage'),
      )
    }

    setIsOpenDuplicateModal(false)
  }

  const tabsContent: Record<string, ReactNode> = {
    all: (
      <div className={styles['all-container']}>
        <div>
          <CardInfo
            title={t('quotationExpiresAtTitle')}
            icon={<CalendarIcon />}
          >
            {!isLoading ? (
              <Text>{formatDate(dataQuotation?.data?.expiresAt || '')}</Text>
            ) : (
              <Skeleton height="1.2rem" width="10rem" />
            )}
          </CardInfo>

          <QuotationSummaryStateHistory
            isLoading={isLoading}
            createdAt={dataQuotation?.data?.createdAt}
            quotationRequest={dataQuotation?.data?.quotationRequest}
            orders={dataQuotation?.data?.orders}
            creatorEmail={dataQuotation?.data?.creatorEmail}
            keyAccountManager={
              dataQuotation?.data?.buyerProfile
                ?.keyAccountManager as unknown as string
            }
            disableNotifications={
              dataQuotation?.data?.disableNotifications || false
            }
            uuid={dataQuotation?.data?.uuid || ''}
            acceptorUser={dataQuotation?.data?.acceptorUser}
          />
        </div>

        <div>
          <QuotationSummaryProfile
            uuid={dataQuotation?.data?.buyerProfile?.uuid || ''}
            companyName={dataQuotation?.data?.buyerProfile?.companyName || ''}
            email={dataQuotation?.data?.buyerProfile?.email || ''}
            name={
              dataQuotation?.data?.buyerProfile?.usersNames
                ? dataQuotation?.data?.buyerProfile?.usersNames.join(', ')
                : '-'
            }
            phone={dataQuotation?.data?.buyerProfile?.phone || ''}
            moscow={
              dataQuotation?.data?.buyerProfile?.moscowClassification || ''
            }
            customerLevel={
              (dataQuotation?.data?.buyerProfile
                ?.customerLevel as unknown as string) || ''
            }
            isLoading={isLoading}
          />

          <QuotationSummaryValues
            isLoading={isLoading}
            discount={discount}
            iva={iva}
            subTotal={subTotal}
            total={total}
            totalFreight={
              dataQuotation?.data?.deliveryOption === 'pick_up'
                ? 0
                : +(dataQuotation?.data?.totalFreightPrice || 0)
            }
            paymentForm={dataQuotation?.data?.paymentFormOption || ''}
          />
        </div>
      </div>
    ),
    products: (
      <>
        <QuotationSummaryProducts
          isLoading={isLoading}
          products={dataQuotation?.data?.products || []}
          dollarQuotation={dataQuotation?.data?.dollarExchangeRate || 0}
        />
      </>
    ),
    logistic: (
      <>
        <QuotationSummaryDelivery
          deliveryAddress={dataQuotation?.data?.deliveryAddress}
          delivery={dataQuotation?.data?.deliveryOption || ''}
          dollarQuotation={dataQuotation?.data?.dollarExchangeRate || 0}
          totalFreightPrice={dataQuotation?.data?.totalFreightPrice || 0}
          firstMileFreightCost={dataQuotation?.data?.firstMileFreightCost || 0}
          lastMileFreightCost={dataQuotation?.data?.lastMileFreightCost || 0}
          customsFreightCost={dataQuotation?.data?.customsFreightCost || 0}
          financingCost={Number(dataQuotation?.data?.financingCost || 0)}
          expectedDeliveryDays={dataQuotation?.data?.expectedDeliveryDays}
          isLoading={isLoading}
        />
      </>
    ),
    history: (
      <>
        <History />
      </>
    ),
    aiData: (
      <>
        <AiData aiComplementData={dataQuotation?.data?.aiComplementData} />
      </>
    ),
  }

  const { data: dataCalculateMargin, mutateAsync: mutateAsyncCalculateMargin } =
    useCalculateMargin()

  const incomplete = useMemo(
    () => !dataCalculateMargin?.success,
    [dataCalculateMargin],
  )

  useEffect(() => {
    if (
      dataForMetrics &&
      dataForMetrics?.customsFreightCost &&
      Object.keys(dataForMetrics).length > 0
    )
      mutateAsyncCalculateMargin(dataForMetrics as CalculateMarginPayloadProps)
  }, [dataForMetrics])

  useEffect(() => {
    trackEvent('adminQuotationShowPageView', { page })
  }, [])

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <Text variant="text-small" className={styles.breadcrumb}>
          <Link
            to={ROUTES_ADMIN.BUSINESS.QUOTATION.fullPath()}
          >{`${t('quotationTitle')}`}</Link>
          {` / `}
          <span>{dataQuotation?.data?.reference}</span>
        </Text>

        <div className={styles['header-content']}>
          <div>
            <div className={styles['sub-title']}>
              <Text variant="title-large">
                {t(
                  dataQuotation?.data?.products?.length === 1
                    ? 'quotationIdentifier'
                    : 'quotationIdentifierPlural',
                  {
                    quotation: dataQuotation?.data?.reference,
                    quantity: dataQuotation?.data?.products?.length,
                  },
                )}
              </Text>

              <QuotationStatusSelect quotation={dataQuotation?.data} />

              {dataQuotation?.data?.isAutomated && (
                <div className={styles.automated}>
                  <Tooltip text={t('quotationIsAutomated')} orientation="right">
                    <StarsIcon />
                  </Tooltip>
                </div>
              )}

              {dataQuotation?.data?.isAiAutomated && (
                <div className={styles['automated-ai']}>
                  <Tooltip
                    text={t('quotationIsAiAutomated')}
                    orientation="right"
                  >
                    <StarsIcon />
                  </Tooltip>
                </div>
              )}

              {dataQuotation?.data?.isSample && (
                <Text variant="title-large" className={styles['sample-title']}>
                  {t('quotationTitleIsSample')}
                </Text>
              )}
            </div>
            <Text
              variant="text-base"
              className={styles['sub-title-instructions']}
            >
              {t('instructionGenerateProposals')}
            </Text>
          </div>

          <div className={styles['header-actions']}>
            {(isPending || isCreated) && (
              <Button
                variant="secondary"
                href={ROUTES_ADMIN.BUSINESS.QUOTATION.UPDATE.fullPath(
                  uuid || '',
                )}
              >
                {t('quotationUpdateQuotationButton')}
              </Button>
            )}
            {isPendingApproval && hasPendingApprovalPermission && (
              <Button
                variant="secondary"
                onClick={() => setIsOpenPendingApproval(true)}
              >
                {t('quotationApprovalQuotationButton')}
              </Button>
            )}

            <Button
              onClick={() => {
                if (!dontShowDuplicateWarning) {
                  setIsOpenDuplicateModal(true)
                } else {
                  onHandleDuplicateQuotation()
                }
              }}
              variant="secondary"
            >
              {t('quotationShowDuplicateButton')}
            </Button>

            <Button href={appQuotationLink(uuid || '')} target="_blank">
              {t('quotationShowInMercaiOsButton')}
            </Button>
          </div>
        </div>
      </header>

      <Tabs
        items={[
          {
            title: t('quotationTabAll'),
            isActive: queryParams.tab === 'all' || !queryParams.tab,
            onSelect: () => handleParams('tab', 'all'),
          },
          {
            title: t('quotationTabProducts'),
            isActive: queryParams.tab === 'products',
            onSelect: () => handleParams('tab', 'products'),
          },
          {
            title: t('quotationTabLogistic'),
            isActive: queryParams.tab === 'logistic',
            onSelect: () => handleParams('tab', 'logistic'),
          },
          {
            title: t('quotationTabHistory'),
            isActive: queryParams.tab === 'history',
            onSelect: () => handleParams('tab', 'history'),
          },
          ...(dataQuotation?.data?.isAiAutomated
            ? [
                {
                  title: t('quotationTabAiData'),
                  isActive: queryParams.tab === 'aiData',
                  onSelect: () => handleParams('tab', 'aiData'),
                },
              ]
            : []),
        ]}
      />

      <QuotationMetrics
        values={dataCalculateMargin?.data}
        incomplete={incomplete}
      />

      <div className={styles.content}>
        {tabsContent?.[queryParams?.tab || 'all']}
      </div>

      <PendingApprovalModal
        quotation={dataQuotation?.data}
        isOpen={isOpenPendingApproval}
        onClose={() => setIsOpenPendingApproval(false)}
      />

      <DuplicateQuotationModal
        isOpen={isOpenDuplicateModal}
        onHandleClose={() => {
          setIsOpenDuplicateModal(false)
        }}
        onHandleSubmit={onHandleDuplicateQuotation}
      />
    </div>
  )
}
